import { Badge, Box, Float, Table } from "@chakra-ui/react";
import { formatAttemptResult } from "@/lib/wca/wcif/attempts";
import _ from "lodash";
import type { ReactNode } from "react";
import { components } from "@/types/openapi";
import { TFunction } from "i18next";

/**
 * Which ranking a table is listing: the results of one round, or the final standing a competitor
 * reached. They only differ for a Dual Round, where a competitor holds a result in each round but
 * is ranked once between them.
 */
type RankingVariant = "round" | "standings";

export function PositionCell({
  result,
  rankingMode,
  variant = "round",
}: {
  result: Pick<components["schemas"]["V1RoundResult"], "pos" | "global_pos">;
  rankingMode: components["schemas"]["RankingMode"];
  variant?: RankingVariant;
}) {
  // A standings row is already merged across the Dual Round, keeping only the competitor's better
  // result, so the position within whichever round that came from would be meaningless here.
  if (variant === "standings") {
    return <Table.Cell>{result.global_pos}</Table.Cell>;
  }

  if (rankingMode !== "linked_round") {
    return <Table.Cell>{result.pos}</Table.Cell>;
  }

  return (
    <Table.Cell>
      {result.pos} ({result.global_pos})
    </Table.Cell>
  );
}

export function RoundNameCell({
  roundTypeId,
  rankingMode,
  t,
}: {
  roundTypeId: string;
  rankingMode: components["schemas"]["RankingMode"];
  t: TFunction;
}) {
  const roundName = t(`rounds.${roundTypeId}.name`);

  return (
    <Table.Cell>
      {rankingMode === "linked_round"
        ? `${roundName} (${t("persons.show.dual")})`
        : roundName}
    </Table.Cell>
  );
}

const recordTagBadge = (tag?: string | null) => {
  switch (tag) {
    case "WR": {
      return { color: "red", label: "WR" };
    }
    case "AfR":
    case "AsR":
    case "ER":
    case "NAR":
    case "OcR":
    case "SAR": {
      return { color: "yellow", label: "CR" };
    }
    case "NR": {
      return { color: "green", label: "NR" };
    }
    case "PR": {
      return { color: "blue", label: "PR" };
    }
    default: {
      return null;
    }
  }
};

// Width reserved at the end of the value for the floating badge: `Float` is
// absolutely positioned and contributes no width of its own, so without it the
// badge overlaps whatever sits in the next column.
const RECORD_TAG_SLOT = "7";

// Renders the record badge as a superscript floating off the top-right corner
// of its content, without overflowing the surrounding text's line box.
export function WithRecordTag({
  recordTag,
  children,
}: {
  recordTag?: string | null;
  children: ReactNode;
}) {
  const badge = recordTagBadge(recordTag);

  if (!badge) return children;

  return (
    <Box
      as="span"
      position="relative"
      display="inline-block"
      paddingEnd={RECORD_TAG_SLOT}
    >
      {children}
      <Float placement="top-end" offsetX="3.5" offsetY="1">
        <Badge
          size="xs"
          variant="solid"
          colorPalette={badge.color}
          minHeight="3.5"
        >
          {badge.label}
        </Badge>
      </Float>
    </Box>
  );
}

interface AttemptsCellProps {
  attempts: number[];
  bestResultIndex: number;
  worstResultIndex: number;
  eventId: string;
  recordTag?: string | null;
  attemptCount: number;
}

export function AttemptsCells({
  attempts,
  bestResultIndex,
  worstResultIndex,
  eventId,
  recordTag,
  attemptCount,
}: AttemptsCellProps) {
  return _.times(attemptCount).map((a) => {
    const attempt = attempts[a];
    const key = `attempt-${attempt}-${a}`;

    if (!attempt) return <Table.Cell key={key} />;

    return (
      // One Cell per Solve of an Average. The exact same result may occur multiple times
      //   in the same average (think FMC), so we use the iteration index as key.
      <Table.Cell key={key}>
        <WithRecordTag recordTag={bestResultIndex === a ? recordTag : null}>
          {attempts.filter(Boolean).length === 5 &&
          (a === bestResultIndex || a === worstResultIndex)
            ? `(${formatAttemptResult(attempt, eventId)})`
            : formatAttemptResult(attempt, eventId)}
        </WithRecordTag>
      </Table.Cell>
    );
  });
}

// Mirrors the old Rails `pb_type_class_for_result`: a result that was a personal
// best at the time it was achieved is coloured, upgraded to the record's own
// colour when it was also a regional record.
export function personalBestColor(regionalRecord?: string | null) {
  switch (regionalRecord) {
    case "WR":
      return "recordMarkers.world";
    case "NR":
      return "recordMarkers.national";
    case "":
    case null:
    case undefined:
      return "recordMarkers.personal";
    default:
      return "recordMarkers.continental";
  }
}
